import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProfile } from './ProfileContext';
import { supabase } from './supabaseClient';
import AppNav from './AppNav';
import { Reveal } from './animations';
import { normalizeBranch } from './branch';
import './Card.css';
import './FeedPage.css';

// Backed by public.news_cache, populated every 6 hours by the fetch-news
// Edge Function from Military Times, Stars and Stripes, VA News, Department
// of War, and Task & Purpose — see supabase/functions/fetch-news. Only
// categories with real, non-trivial coverage from those sources are listed;
// a "Markets"/"Technology"/"Wellness" tab would just show empty most days.
const CATEGORIES = [
  { key: 'all',      label: 'All Intel' },
  { key: 'defense',  label: 'Defense' },
  { key: 'jobs',     label: 'Veteran Jobs' },
  { key: 'benefits', label: 'VA & Benefits' },
];

const CATEGORY_FILTERS = {
  defense:  /defense department|pentagon|military contract|\bDoD\b/i,
  jobs:     /veteran hiring|transition jobs|cleared jobs|hiring|job fair|career fair/i,
  benefits: /VA disability|GI Bill|veteran benefits|VA healthcare|\bVA\b/i,
};

function dedupeArticles(articles) {
  const seen = new Set();
  const result = [];
  for (const article of articles) {
    const key = (article.title || '').trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(article);
  }
  return result;
}

function timeAgo(dateStr) {
  const date = new Date(dateStr);
  if (!dateStr || Number.isNaN(date.getTime())) return 'Publication date unavailable';
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 0 || diff >= 604800) return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (diff < 60) return 'Just published';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Civilian-first identity rendering for VetQualified.
// Composes a polished display label from the user's chosen display_identity
// category code plus their branch. Stable storage codes; rendering changes
// without database migrations.
function renderIdentity(identity, branch) {
  const b = normalizeBranch(branch);
  switch (identity) {
    case 'retired_veteran':
      return b ? `Retired ${b} Veteran` : 'Retired Veteran';
    case 'veteran':
      return b ? `${b} Veteran` : 'Veteran';
    case 'transitioning_service_member':
      return b ? `Transitioning ${b} Service Member` : 'Transitioning Service Member';
    case 'active_duty':
      return b ? `Active Duty ${b}` : 'Active Duty';
    case 'guard_reserve':
      return b ? `${b} Guard/Reserve` : 'Guard/Reserve';
    case 'military_spouse':
      return 'Military Spouse';
    case 'family_member':
      return 'Military Family Member';
    case 'supporter':
      return 'VetQualified Supporter';
    default:
      return 'VetQualified Member';
  }
}

function ArticleCard({ article }) {
  const [imageFailed, setImageFailed] = useState(false);
  return (
    <a
      className="feed-card vq-card vq-card--interactive"
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {article.image_url && !imageFailed && (
        <div className="feed-card-img-wrap">
          <img
            src={article.image_url}
            alt=""
            className="feed-card-img"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        </div>
      )}
      <div className="feed-card-body">
        <div className="feed-card-meta">
          <span className="feed-card-source">{article.source}</span>
          <span className="feed-card-dot">·</span>
          <time className="feed-card-time" dateTime={article.published_at || undefined}>{timeAgo(article.published_at)}</time>
        </div>
        <h2 className="feed-card-title">{article.title}</h2>
        {article.description && (
          <div className="feed-card-desc">{article.description}</div>
        )}
        <span className="feed-card-read">Read at source <span aria-hidden="true">↗</span><span className="feed-sr-only"> (opens in a new tab)</span></span>
      </div>
    </a>
  );
}

export default function FeedPage() {
  const { profile } = useProfile();
  const [articles,       setArticles]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setArticles([]);
    setError(null);

    supabase
      .from('news_cache')
      .select('*')
      .order('published_at', { ascending: false })
      .then(({ data, error: fetchError }) => {
        if (cancelled) return;
        if (fetchError || !data) {
          setError('Failed to load feed. Please try again.');
          setLoading(false);
          return;
        }
        const filter = CATEGORY_FILTERS[activeCategory];
        const matched = filter
          ? data.filter(a => filter.test(`${a.title} ${a.description || ''}`))
          : data;
        const deduped = dedupeArticles(matched);
        setArticles(deduped);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeCategory, retry]);

  const rawInitials =
    `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`.toUpperCase();
  const initials = rawInitials || 'VQ';

  const hasName = profile.firstName || profile.lastName;
  const displayName = hasName
    ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
    : 'Complete your profile';

  // Civilian-first identity line. Rank is intentionally not shown on the
  // Feed profile card by default.
  const serviceLine = renderIdentity(profile.displayIdentity, profile.branch);

  return (
    <div className="feed-root vq-surface">
      <AppNav />
      <header className="feed-hero">
        <div className="feed-hero-copy">
          <span className="feed-eyebrow">Intel feed · The veteran perspective</span>
          <h1>Stay informed.<br />Keep moving forward.</h1>
          <p>Military news, career opportunities, and VA updates from the sources you know.</p>
        </div>
        <Link to="/messages" className="feed-message-link">Open messages <span aria-hidden="true">↗</span></Link>
      </header>
      <div className="feed-shell">

        <main className="feed-main" aria-label="Intel articles">
          <div className="feed-section-heading"><h2>Your briefing</h2><span>Newest first · Opens at source</span></div>
          <Reveal delay={0.05}>
            <div className="feed-category-bar" role="group" aria-label="Filter intel">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  className={`feed-cat-btn ${activeCategory === cat.key ? 'active' : ''}`}
                  aria-pressed={activeCategory === cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </Reveal>

          {loading && (
            <div className="feed-loading vq-card" role="status">
              <div className="feed-spinner" />
              <p>Loading your briefing…</p>
            </div>
          )}

          {!loading && error && <div className="feed-error vq-card" role="alert"><h2>Your briefing could not load</h2><p>Check your connection and try again.</p><button onClick={() => setRetry(value => value + 1)}>Try again</button></div>}

          {!loading && !error && articles.length === 0 && <div className="feed-empty vq-card"><h2>No articles in this category yet</h2><p>Articles will appear here as coverage becomes available. Browse All Intel for more from the feed.</p><button onClick={() => setActiveCategory('all')}>Browse All Intel</button></div>}

          {!loading && !error && (
            <Reveal delay={0.1}>
              <div className="feed-articles">
                {articles.map(article => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </Reveal>
          )}
        </main>

        <aside className="feed-left">
          <Reveal direction="left">
            <div className="feed-profile-card vq-card">
              <div className="feed-avatar">
                {profile.avatarUrl
                  ? <img src={profile.avatarUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  : initials
                }
              </div>
              <div className="feed-profile-name">{displayName}</div>
              <div className="feed-profile-rank">{serviceLine}</div>
              <div className="feed-profile-divider" />
              <div className="feed-stat-row">
                <span className="feed-stat-label">Profile Views</span>
                <span className="feed-stat-val">{profile.profileViews}</span>
              </div>
              <div className="feed-stat-row">
                <span className="feed-stat-label">Recruiter Views</span>
                <span className="feed-stat-val">{profile.recruiterViews}</span>
              </div>
              <div className="feed-stat-row">
                <span className="feed-stat-label">Job Matches</span>
                <span className="feed-stat-val">{profile.jobMatches}</span>
              </div>
              <Link to="/dashboard" className="feed-profile-link">View my profile →</Link>
            </div>
          </Reveal>
        </aside>

        <aside className="feed-right">
          <Reveal direction="right" delay={0.1}>
            <div className="feed-sidebar-card vq-card">
              <h2 className="feed-sidebar-title">Keep the conversation going</h2>
              <p className="feed-sidebar-copy">Exchange perspectives with your veteran network.</p>
              <Link to="/messages" className="feed-quick-link">Your messages →</Link>
              <Link to="/forums" className="feed-quick-link">Branch forums →</Link>
            </div>
            <div className="feed-sidebar-card vq-card">
              <h2 className="feed-sidebar-title">Career resources</h2>
              {[
                { label: 'USAJobs.gov',   url: 'https://usajobs.gov' },
                { label: 'ClearanceJobs', url: 'https://clearancejobs.com' },
                { label: 'VA Careers',    url: 'https://vacareers.va.gov' },
                { label: 'DoD COOL',      url: 'https://dodcool.org' },
              ].map(l => (
                <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer" className="feed-quick-link">
                  {l.label} →
                </a>
              ))}
            </div>
          </Reveal>
        </aside>

      </div>
    </div>
  );
}